import { isDetachedMode } from './embedded-mode';

export const DETACHED_SESSION_STORAGE_PREFIX = 'casino-detached-session:';
export const DETACHED_GAME_WINDOW_PATH = '/game-window-play';

export type DetachedSessionPayload = Record<string, never>;

const getStorageKey = (transferId: string): string =>
  `${DETACHED_SESSION_STORAGE_PREFIX}${transferId}`;

export const saveDetachedSession = (
  transferId: string,
  payload: DetachedSessionPayload,
): void => {
  localStorage.setItem(getStorageKey(transferId), JSON.stringify(payload));
};

export const loadDetachedSession = (
  transferId: string,
): DetachedSessionPayload | null => {
  const raw = localStorage.getItem(getStorageKey(transferId));
  return raw ? (JSON.parse(raw) as DetachedSessionPayload) : null;
};

export const clearDetachedSession = (transferId: string): void => {
  localStorage.removeItem(getStorageKey(transferId));
};

export const clearDetachedHandoffQueryParams = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  if (!url.searchParams.has('mode') && !url.searchParams.has('transferId')) {
    return;
  }

  url.searchParams.delete('mode');
  url.searchParams.delete('transferId');
  window.history.replaceState(
    {},
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
};

const parseSourceLauncherUrl = (sourceLauncherUrl?: string): URL | null => {
  if (!sourceLauncherUrl) {
    return null;
  }

  try {
    return new URL(sourceLauncherUrl, window.location.origin);
  } catch {
    return null;
  }
};

const extractPathGameUid = (pathname: string): string | undefined => {
  const normalized = pathname.replace(/\/$/, '');
  const prefix = `${DETACHED_GAME_WINDOW_PATH}/`;

  if (!normalized.startsWith(prefix) || normalized.length <= prefix.length) {
    return undefined;
  }

  const pathGameUid = normalized.slice(prefix.length);
  return pathGameUid && !pathGameUid.includes('/') ? pathGameUid : undefined;
};

const isFgpQueryLaunch = (source: URL): boolean =>
  source.searchParams.has('providerName') && source.searchParams.has('gameId');

const buildDetachedShortLinkUrl = (
  transferId: string,
  gameUid: string,
): string => {
  const url = new URL(
    `${window.location.origin}${DETACHED_GAME_WINDOW_PATH}/${gameUid}`,
  );
  url.searchParams.set('mode', 'detached');
  url.searchParams.set('transferId', transferId);
  return url.toString();
};

export const buildDetachedLauncherUrl = (
  transferId: string,
  sourceLauncherUrl?: string,
): string => {
  const source = parseSourceLauncherUrl(sourceLauncherUrl);

  if (source) {
    const pathGameUid = extractPathGameUid(source.pathname);
    const queryGameUid = source.searchParams.get('gameUid');
    const shortLinkGameUid =
      pathGameUid ?? (!isFgpQueryLaunch(source) ? queryGameUid : null);

    if (shortLinkGameUid) {
      return buildDetachedShortLinkUrl(transferId, shortLinkGameUid);
    }

    const url = new URL(
      `${window.location.origin}${DETACHED_GAME_WINDOW_PATH}/`,
    );
    source.searchParams.forEach((value, key) => {
      if (key !== 'embedded' && key !== 'mode' && key !== 'transferId') {
        url.searchParams.set(key, value);
      }
    });
    url.searchParams.set('mode', 'detached');
    url.searchParams.set('transferId', transferId);
    return url.toString();
  }

  const url = new URL(`${window.location.origin}${DETACHED_GAME_WINDOW_PATH}/`);
  url.searchParams.set('mode', 'detached');
  url.searchParams.set('transferId', transferId);
  return url.toString();
};

export const createTransferId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `detach-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

type PrepareDetachedNavigationParams = {
  targetUrl: string;
};

export const prepareDetachedNavigation = ({
  targetUrl,
}: PrepareDetachedNavigationParams): {
  detachedUrl?: string;
  transferId: string | null;
} => {
  if (isDetachedMode()) {
    return { transferId: null, detachedUrl: targetUrl };
  }

  const transferId = createTransferId();

  saveDetachedSession(transferId, {});

  return { transferId };
};
